import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, CheckCircle2, AlertCircle, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { siteContent } from "@/content/site";

const { betaSignup } = siteContent;

export default function BetaSignup() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    senior_location: "",
    role: betaSignup.roles[0]?.v || "adult_child",
    consent_privacy_notice: false,
  });
  const [status, setStatus] = useState({ type: "idle", message: "" });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setStatus({ type: "error", message: betaSignup.errors.missingFields });
      return;
    }
    if (!form.senior_location) {
      setStatus({ type: "error", message: betaSignup.errors.missingSeniorLocation });
      return;
    }
    if (!form.consent_privacy_notice) {
      setStatus({ type: "error", message: betaSignup.errors.missingConsent });
      return;
    }
    setStatus({ type: "loading", message: "" });
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        city: form.city.trim() || null,
        senior_location: form.senior_location,
        role: form.role,
        consent_privacy_notice: true,
      };
      await api.post("/beta-signup", payload);
      setStatus({ type: "success", message: betaSignup.success });
      setForm({
        name: "",
        email: "",
        phone: "",
        city: "",
        senior_location: "",
        role: betaSignup.roles[0]?.v || "adult_child",
        consent_privacy_notice: false,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || betaSignup.errors.fallback;
      setStatus({ type: "error", message: detail });
    }
  };

  return (
    <section
      id="beta"
      data-testid="beta-section"
      className="relative py-20 lg:py-28 bg-[#F4FAF8] overflow-hidden"
    >
      <div className="absolute inset-0 -z-0">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-[#5BAE71]/15 blur-3xl" />
      </div>
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="bg-[#1F4E5F] text-[#F4FAF8] rounded-[36px] p-8 sm:p-12 lg:p-16 relative overflow-hidden border border-[#1F4E5F]"
        >
          <div className="grain absolute inset-0 pointer-events-none" />
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#E8A33D]/30 blur-3xl" />

          <div className="relative grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.18em] text-[#E8A33D]">
                <Sparkles className="w-3 h-3" /> {betaSignup.eyebrow}
              </span>
              <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-[44px] font-bold leading-tight">
                {betaSignup.headingLead}{" "}
                <em className="italic font-medium text-[#E8A33D]">
                  {betaSignup.headingItalic}
                </em>
                {betaSignup.headingTail}
              </h2>
              <p className="mt-4 text-[#F4FAF8]/80 text-[15px] leading-relaxed">
                {betaSignup.body}
              </p>
              <ul className="mt-6 space-y-2.5 text-sm text-[#F4FAF8]/85">
                {betaSignup.perks.map((p, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#E8A33D]" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <form
              onSubmit={onSubmit}
              className="lg:col-span-3 bg-[#F4FAF8] text-[#0E2A36] rounded-3xl p-6 sm:p-8 space-y-4"
              data-testid="beta-form"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={betaSignup.fields.nameLabel} id="beta-name">
                  <input
                    id="beta-name"
                    type="text"
                    data-testid="beta-input-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder={betaSignup.fields.namePlaceholder}
                    className="w-full bg-white border border-[#C9DDD6] rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition"
                  />
                </Field>
                <Field label={betaSignup.fields.emailLabel} id="beta-email">
                  <input
                    id="beta-email"
                    type="email"
                    data-testid="beta-input-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder={betaSignup.fields.emailPlaceholder}
                    className="w-full bg-white border border-[#C9DDD6] rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition"
                  />
                </Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={betaSignup.fields.phoneLabel} id="beta-phone">
                  <input
                    id="beta-phone"
                    type="tel"
                    data-testid="beta-input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder={betaSignup.fields.phonePlaceholder}
                    className="w-full bg-white border border-[#C9DDD6] rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition"
                  />
                </Field>
                <Field label={betaSignup.fields.cityLabel} id="beta-city">
                  <input
                    id="beta-city"
                    type="text"
                    data-testid="beta-input-city"
                    value={form.city}
                    onChange={(e) => update("city", e.target.value)}
                    placeholder={betaSignup.fields.cityPlaceholder}
                    className="w-full bg-white border border-[#C9DDD6] rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition"
                  />
                </Field>
              </div>

              <Field
                label={betaSignup.fields.seniorLocationLabel}
                id="beta-senior-location"
              >
                <select
                  id="beta-senior-location"
                  data-testid="beta-input-senior-location"
                  value={form.senior_location}
                  onChange={(e) => update("senior_location", e.target.value)}
                  required
                  className="w-full bg-white border border-[#C9DDD6] rounded-xl px-4 py-3 text-[15px] text-[#0E2A36] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition appearance-none"
                >
                  <option value="" disabled>
                    {betaSignup.fields.seniorLocationPlaceholder}
                  </option>
                  {betaSignup.seniorLocations.map((g) => (
                    <optgroup key={g.group} label={g.group}>
                      {g.items.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </Field>

              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#3D5560] mb-2">
                  {betaSignup.fields.roleLabel}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {betaSignup.roles.map((r) => {
                    const active = form.role === r.v;
                    return (
                      <button
                        key={r.v}
                        type="button"
                        data-testid={`beta-role-${r.v}`}
                        onClick={() => update("role", r.v)}
                        className={`px-3 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                          active
                            ? "bg-[#1F4E5F] text-[#F4FAF8] border-[#1F4E5F]"
                            : "bg-white text-[#0E2A36] border-[#C9DDD6] hover:border-[#1F4E5F]/40"
                        }`}
                      >
                        {r.l}
                      </button>
                    );
                  })}
                </div>
              </div>

              {status.type === "error" && (
                <div
                  data-testid="beta-error"
                  className="flex items-center gap-2 text-[#C88A28] bg-[#E8A33D]/10 border border-[#E8A33D]/25 rounded-xl px-3 py-2.5 text-sm"
                >
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{status.message}</span>
                </div>
              )}
              {status.type === "success" && (
                <div
                  data-testid="beta-success"
                  className="flex items-center gap-2 text-[#2D7A4A] bg-[#5BAE71]/15 border border-[#5BAE71]/30 rounded-xl px-3 py-2.5 text-sm"
                >
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>{status.message}</span>
                </div>
              )}

              <label
                htmlFor="beta-consent"
                className="flex items-start gap-3 cursor-pointer select-none rounded-xl bg-white border border-[#C9DDD6] p-3.5 hover:border-[#1F4E5F]/40 transition-colors"
              >
                <input
                  id="beta-consent"
                  type="checkbox"
                  data-testid="beta-input-consent"
                  checked={form.consent_privacy_notice}
                  onChange={(e) => update("consent_privacy_notice", e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-[#C9DDD6] text-[#1F4E5F] focus:ring-[#1F4E5F] accent-[#1F4E5F] shrink-0"
                />
                <span className="text-[13px] leading-relaxed text-[#3D5560]">
                  {betaSignup.consentPrefix}
                  <Link
                    to="/terms-and-policies#privacy-notice"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1F4E5F] font-semibold underline underline-offset-2 decoration-[#C9DDD6] hover:decoration-[#1F4E5F]"
                  >
                    {betaSignup.consentPrivacyLabel}
                  </Link>
                  {betaSignup.consentMiddle}
                  <Link
                    to="/terms-and-policies#terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1F4E5F] font-semibold underline underline-offset-2 decoration-[#C9DDD6] hover:decoration-[#1F4E5F]"
                  >
                    {betaSignup.consentTermsLabel}
                  </Link>
                  {betaSignup.consentTail}
                </span>
              </label>

              <button
                type="submit"
                data-testid="beta-submit"
                disabled={
                  status.type === "loading" ||
                  !form.consent_privacy_notice ||
                  !form.senior_location
                }
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] px-6 py-3.5 rounded-full text-[15px] font-semibold transition-all hover:gap-3 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:gap-2"
              >
                {status.type === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> {betaSignup.submitting}
                  </>
                ) : (
                  <>
                    {betaSignup.submit} <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              <p className="text-[11px] text-[#6A8290] text-center">
                {betaSignup.submitFooter}
              </p>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-bold uppercase tracking-[0.14em] text-[#3D5560] mb-2"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
